import { apiClient, getApiBaseUrl, tokenStorage } from "./client";
import type { CfoQueueCounts, CfoWorkItem, Company, EnrichmentMember } from "./types";

/** `this_month` is the current calendar month (UTC): the pay period, whose
 *  total only grows. `month` is a rolling 30 days, whose total drops as its
 *  oldest day leaves the window. */
export type EnrichmentStatsPeriod = "week" | "month" | "this_month" | "all";

/** WorkCompleteRequest: the server computes the changed fields and commits
 *  contact, routing, activity and release together. Retry the identical body
 *  and request_id when the outcome of a request is unknown. */
export interface WorkCompleteRequest {
  request_id: string;
  contact_id: number | null;
  expected_contact_updated_at: string | null;
  full_name: string;
  title: string | null;
  phone: string | null;
  email: string | null;
  linkedin_url: string | null;
  time_spent_seconds: number;
}

/** Mirrors WorkCompleteResponse in the locally exported OpenAPI contract. */
export interface WorkCompleteResponse {
  company_id: number;
  contact_id: number;
  activity_log_id: number;
  pipeline_status: string;
  changes: string[];
  request_id: string;
  routing_skipped_reason?: string | null;
}

export async function completeEnrichmentWork(
  slug: string,
  companyId: number,
  body: WorkCompleteRequest,
): Promise<WorkCompleteResponse> {
  const res = await apiClient.post<WorkCompleteResponse>(
    `/projects/${slug}/enrichment/work/${companyId}/complete`,
    body,
  );
  return res.data;
}

export interface EnrichmentDailyEntry {
  date: string;
  enriched: number;
  with_contact: number;
  earnings: number;
  /** Corrections closed that day. Unpaid rework, so it never feeds
   *  `earnings` — plotted as its own series. */
  corrections: number;
}

/** Per-field unit prices (USD per lead) mirrored from
 *  `project.config.enrichment_rates.default` (or the per-user
 *  override when the operator has one). The backend resolves the
 *  applicable rates for the current user before sending so the
 *  frontend can drive the rate card without a second lookup.
 *
 *  Wire-level the backend types this as `Record<string, number>`
 *  (additionalProperties: number) — the 4 known keys are guaranteed
 *  by the seed + admin UI, extras are tolerated and ignored. */
export interface EnrichmentRates {
  name: number;
  linkedin: number;
  phone: number;
  email: number;
}

/** Mirrors EnrichmentStatsResponse. Scope and currency are decided by the server. */
export interface EnrichmentStatsResponse {
  scope: "self" | "team";
  /** Selected operator, or null for the authorized team. */
  user_id: string | null;
  currency: "USD";
  total_enriched: number;
  with_phone_and_email: number;
  with_phone_only: number;
  with_email_only: number;
  not_found: number;
  /** Corrections closed over the period. Counted for activity and
   *  streak, excluded from every earnings figure. */
  total_corrections: number;
  avg_time_seconds: number;
  linkedin_fill_rate: number;
  contact_rate: number;
  streak: number;
  daily: EnrichmentDailyEntry[];
  /** Resolved per-field rate card for the current user. */
  rates: EnrichmentRates;
  /** Authoritative total — sum of (field count × field rate) over
   *  the period, computed server-side. The frontend reads this
   *  verbatim for the headline number; per-field counts aren't
   *  shipped so the UI shows the rate card rather than a
   *  count × rate breakdown. */
  total_earnings: number;
  not_found_by_reason: Record<string, number>;
}

/** Currency and numeric rates are returned together; no conversion is applied. */
export interface EnrichmentRatesResponse {
  user_id: string;
  currency: "USD";
  rates: Record<string, number>;
}

export async function getEnrichmentRates(slug: string): Promise<EnrichmentRatesResponse> {
  const { data } = await apiClient.get<EnrichmentRatesResponse>(`/projects/${slug}/enrichment-rates`);
  return data;
}

// ─── Corrections queue ─────────────────────────────────────────────────────

/** One free-text note the sales side left on the company. Aggregated
 *  server-side from company notes, activity rows, contact notes and
 *  tasks, so `source` labels which of those it came from. */
export interface RecentComment {
  source: string;
  author: string | null;
  created_at: string | null;
  text: string;
}

/** Current (wrong) contact data shipped with a correction item, so the
 *  form can pre-fill rather than start blank. Single `full_name` — the
 *  backend contact model has no first/last split. */
export interface CorrectionContact {
  id: number;
  full_name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  /** Withheld from this reader (see `Company.hidden_details` in types.ts). */
  hidden_details?: string[];
  linkedin_url: string | null;
  is_decision_maker: boolean;
}

export interface CorrectionQueueItem {
  company_id: number;
  name: string;
  domain: string | null;
  sector: string | null;
  city: string | null;
  country: string | null;
  main_phone: string | null;
  /** Withheld from this reader (see `Company.hidden_details` in types.ts). */
  hidden_details?: string[];
  score: number | null;
  pipeline_status: string;
  contact: CorrectionContact | null;
  recent_comments: RecentComment[];
}

export interface CorrectionQueueResponse {
  items: CorrectionQueueItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface CorrectionCompleteResponse {
  company_id: number;
  tag_removed: boolean;
  pipeline_status: string;
  status_updated: boolean;
  /** Why the status was left alone: "no_phone",
   *  "already_to_be_contacted" or "status_not_in_pipeline". */
  status_skipped_reason: string | null;
  activity_log_id: number;
}

/** Companies flagged "Wrong Owner" that the contractor must re-check.
 *  Served at ANY pipeline_status, ordered by effective score desc.
 *
 *  @deprecated for the operator flow. The Corrections tab now works a
 *  reserved batch from `startEnrichmentSession` — reading this global list
 *  is what let two operators be handed the same company. Kept because the
 *  endpoint still exists and a read-only backlog view is a plausible use;
 *  do NOT wire it back into the working queue. */
export async function getCorrectionsQueue(
  slug: string,
  page = 1,
  pageSize = 25,
): Promise<CorrectionQueueResponse> {
  const res = await apiClient.get<CorrectionQueueResponse>(
    `/projects/${slug}/enrichment/corrections-queue`,
    { params: { page, page_size: pageSize } },
  );
  return res.data;
}

/** Closes a correction: drops the "Wrong Owner" tag, moves the company
 *  to to_be_contacted when the contact now has a phone, and logs the
 *  work as unpaid — all in one backend transaction.
 *
 *  Call AFTER patching the contact; this does not write contact fields.
 *  Idempotent, so retrying a failed call is safe. */
export async function completeCorrection(
  slug: string,
  companyId: number,
  body: { changes: string[]; time_spent_seconds: number },
): Promise<CorrectionCompleteResponse> {
  const res = await apiClient.post<CorrectionCompleteResponse>(
    `/projects/${slug}/enrichment/corrections/${companyId}/complete`,
    body,
  );
  return res.data;
}

/** Performance + earnings rollup for the Enrichment Workstation Stats
 *  tab. The backend endpoint may not be deployed yet — the helper
 *  returns null on any failure so the tab renders a "not yet
 *  available" empty state instead of crashing. */
interface EnrichmentMembersResponse {
  members: EnrichmentMember[];
}

/** Project members whose role carries `enrichment.work` — the audience
 *  the Settings > Enrichment Rates page configures rate cards for.
 *  Server-side filtering means the UI doesn't have to walk every role
 *  client-side to figure out which members get a rate card row. */
export async function listEnrichmentMembers(
  slug: string,
): Promise<EnrichmentMember[]> {
  const res = await apiClient.get<EnrichmentMembersResponse>(
    `/projects/${slug}/enrichment-members`,
  );
  return res.data.members ?? [];
}

export async function getEnrichmentStats(
  slug: string,
  period: EnrichmentStatsPeriod,
  userId?: string,
): Promise<EnrichmentStatsResponse | null> {
  try {
    const res = await apiClient.get<EnrichmentStatsResponse>(
      `/projects/${slug}/analytics/enrichment-stats`,
      { params: { period, ...(userId ? { user_id: userId } : {}) } },
    );
    return res.data;
  } catch {
    return null;
  }
}

// ─── Team stats ────────────────────────────────────────────────────────────

/** One operator's line in the per-operator rollup. `corrections` is
 *  counted but earns nothing — unpaid rework, by contract — so it never
 *  contributes to `earnings`. `avg_time_seconds` averages the operator's
 *  paid rows only. */
/** Counts use every completed record, including unpaid corrections, never adjustments. */
export interface RecoveryMeasure {
  count: number;
  percentage: number | null;
}

export interface EnrichmentRecoveryStats {
  processed: number;
  with_phone: RecoveryMeasure;
  with_email: RecoveryMeasure;
  with_both: RecoveryMeasure;
  with_either: RecoveryMeasure;
  without_contact: RecoveryMeasure;
  unknown: RecoveryMeasure;
  snapshot_records: number;
  verified_contact_records: number;
}

export interface EnrichmentOperatorStats {
  user_id: string;
  full_name: string | null;
  email: string | null;
  enriched: number;
  corrections: number;
  not_found: number;
  earnings: number;
  avg_time_seconds: number;
  recovery: EnrichmentRecoveryStats;
  not_found_by_reason: Record<string, number>;
}

/** The footer line: the operator columns summed, plus how many people the
 *  sums cover.
 *
 *  `avg_time_seconds` is the one field that is NOT the sum (or the mean)
 *  of the lines above it — the backend recomputes it over every paid row,
 *  so a 200-record veteran outweighs a one-record newcomer. Averaging the
 *  per-operator averages here would produce a different, wrong number. */
export interface EnrichmentOperatorStatsTotal {
  /** Enriched + not found + corrections; monetary adjustments add no record. */
  processed: number;
  operators: number;
  enriched: number;
  corrections: number;
  not_found: number;
  earnings: number;
  avg_time_seconds: number;
  recovery: EnrichmentRecoveryStats;
  not_found_by_reason: Record<string, number>;
}

export interface EnrichmentTeamStatsResponse {
  currency: "USD";
  /** Decided server-side from `enrichment.manage_rates`, never by the
   *  caller: "self" is the one line the requester is allowed to see,
   *  "team" is everybody. Drive the heading from THIS, not from
   *  `operators.length` — a one-person team is "team" with one line and
   *  a lone operator is "self" with one line. */
  scope: "self" | "team";
  /** Selected operator, or null for the authorized team. */
  user_id: string | null;
  /** Echo of the `period` query param. Meaningless when `start`/`end`
   *  were sent, since those take precedence — render the dates then. */
  period: string;
  /** Inclusive window actually applied. `start` is null for
   *  `period=all` (every row ever logged). */
  start: string | null;
  end: string;
  /** One line per person with at least one row in the window — never a
   *  roster. Empty means nobody worked, not a broken filter. Delivered
   *  sorted by earnings desc then name; keep that order. */
  operators: EnrichmentOperatorStats[];
  total: EnrichmentOperatorStatsTotal;
  /** Exact inclusive bounds for line items. Absent on older deployments. */
  start_at?: string | null;
  end_at?: string;
}

/** Mirrors EnrichmentHistoryItem / EnrichmentHistoryResponse in local OpenAPI.
 *  One activity per row; rates are current, not historical payment snapshots. */
export interface EnrichmentHistoryItem {
  activity_id: number;
  company_id: number | null;
  company_name: string | null;
  created_at: string;
  kind: "enriched" | "correction" | "not_found" | "adjustment";
  fields_found: string[] | null;
  fields_counted: string[];
  amount: number;
  rate_basis?: "current_rates" | "snapshot";
  rate_snapshot?: Record<string, number> | null;
  original_activity_id?: number | null;
  reason?: string | null;
  not_found_reason?: string | null;
}

export interface EnrichmentHistoryResponse {
  scope: "self" | "team";
  user_id: string;
  start_at: string | null;
  end_at: string;
  currency: "USD";
  rate_basis: "current_rates";
  rates: Record<string, number>;
  items: EnrichmentHistoryItem[];
  total: number;
  page: number;
  page_size: number;
}

export async function getEnrichmentHistory(
  slug: string,
  params: { user_id: string; start_at: string | null; end_at: string; page: number },
): Promise<EnrichmentHistoryResponse> {
  const res = await apiClient.get<EnrichmentHistoryResponse>(
    `/projects/${slug}/analytics/enrichment-history`,
    { params },
  );
  return res.data;
}

/** Window for the per-operator rollup. `start`/`end` are inclusive
 *  `yyyy-MM-dd` dates and OVERRIDE `period` — the only way to ask for a
 *  calendar month, which is what payouts are settled on (`period=month`
 *  is a rolling 30 days, which is not the same window). */
export interface EnrichmentTeamStatsParams {
  user_id?: string;
  period?: EnrichmentStatsPeriod;
  start?: string;
  end?: string;
}

/** Per-operator counts, payout and average time, plus the team total.
 *
 *  Requires `enrichment.view_stats` — the permission every operator
 *  already has. The scope of the answer is the SERVER's decision, so
 *  there is deliberately no frontend permission check wrapped around
 *  this: an operator calling it gets their own line and `scope: "self"`,
 *  an admin gets everyone and `scope: "team"`. */
export async function getEnrichmentTeamStats(
  slug: string,
  params: EnrichmentTeamStatsParams,
): Promise<EnrichmentTeamStatsResponse> {
  const res = await apiClient.get<EnrichmentTeamStatsResponse>(
    `/projects/${slug}/analytics/enrichment-team-stats`,
    { params },
  );
  return res.data;
}

// ─── Sessions ──────────────────────────────────────────────────────────────
//
// Both enrichment queues are worked in sessions: the operator picks a queue
// and a batch size, the backend reserves that many rows to them and hides
// them from everyone else. The batch IS the queue — neither tab reads the
// global listing any more.
//
// Releases are mostly implicit. Completing a lead (`enrichment_completed`)
// or a correction (the `complete` endpoint) frees the row server-side in the
// same transaction, so the frontend must NOT also call skip for those. Only
// an explicit skip, ending the session, or the reservation lapsing releases
// a row — and that lapse is sized from the lot, not a flat 3 hours.

/** Which queue a session draws from: the M&A Work and Corrections queues,
 *  or the CFO research queue on a CFO project. */
export type EnrichmentQueue = "work" | "corrections" | "cfo";

/** Sizes offered as one-click presets. NOT a validation list: the backend
 *  takes any integer >= 1, and the real ceiling is a per-project setting
 *  (`session_lock_max_count`, 500 by default) that another project may
 *  raise or lower. Mirroring that ceiling here is exactly the duplicated
 *  declaration CB-12 was about — so the free-entry field sends whatever
 *  the operator types and the 400 names the actual limit. */
export const ENRICHMENT_BATCH_PRESETS = [10, 20, 30, 50, 100, 200, 500] as const;

/** How many rows the batch-size field defaults to. */
export const ENRICHMENT_DEFAULT_BATCH_SIZE = 20;

export interface EnrichmentSessionStartResponse {
  work_diagnostics?: Record<number, MAWorkSelection> | null;
  queue: EnrichmentQueue;
  /** Populated for `queue: "work"` — same shape as the companies list,
   *  primary contact included. Exactly one of leads/corrections is set,
   *  discriminated by `queue`. */
  leads?: Company[];
  /** Populated for `queue: "corrections"` — same items the corrections
   *  queue used to serve, so the existing renderer applies unchanged. */
  corrections?: CorrectionQueueItem[];
  /** Populated for `queue: "cfo"`: the companies whose CFO decision waits
   *  for a person, review first, then the highest score. */
  cfo_items?: CfoWorkItem[];
  /** ISO instant the reservation lapses. Sized from the lot the backend
   *  actually handed out (count x 4 min, floored at 3h and capped at 24h,
   *  all three per-project), so it is NOT a constant 3 hours and cannot be
   *  recomputed from the count on this side. Display it as given, and
   *  derive any "reserved for Nh" copy from `expires_at - now`.
   *
   *  After it lapses the rows become claimable by others; reopening a
   *  session re-reserves whatever is left. */
  expires_at: string;
  /** Rows still available to OTHER operators — not reserved, not expired.
   *  This is the number to show as "remaining in the queue", NOT the size
   *  of the batch just handed out. */
  queue_remaining: number;
}

/** One operator holding rows, as seen by an admin. Covers call-center
 *  sessions too, so `queue` is a free-form string rather than
 *  EnrichmentQueue. */
export interface SessionHolder {
  user_id: string;
  user_name: string | null;
  queue: string;
  held: number;
  oldest_locked_at: string;
  expires_at: string;
}

/** How much work each queue holds right now, before a session exists. */
export interface EnrichmentQueueCounts {
  work: number;
  corrections: number;
  work_selection?: Record<string, MAWorkCount> | null;
  /** A CFO project, for a caller who may see the CFO flags. */
  cfo?: CfoQueueCounts | null;
}

/** Rows each queue could hand out this instant.
 *
 *  Same arithmetic as `queue_remaining` on a started session — eligible
 *  rows minus EVERY live reservation, the caller's own included — so the
 *  number means one thing before and after a session opens. The visible
 *  consequence is intended: reserving 50 drops the counter by 50, because
 *  those 50 are now in a lot rather than in the queue. Re-read it after a
 *  start, an end and a skip.
 *
 *  `corrections: 0` on a project that has never flagged a "Wrong Owner"
 *  is a truthful zero, not a failure — the loud refusal for that case
 *  lives on session start, where an operator is actually blocked.
 *
 *  Permission is `enrichment.access`, so anyone who can see the page can
 *  read this. */
export async function getEnrichmentQueueCounts(
  slug: string,
): Promise<EnrichmentQueueCounts> {
  const res = await apiClient.get<EnrichmentQueueCounts>(
    `/projects/${slug}/enrichment/queue-counts`,
  );
  return res.data;
}

/** Open a session and receive the reserved batch.
 *
 *  A short batch is a normal answer, not an error: asking for 20 against a
 *  queue of 7 returns 7 with `queue_remaining: 0`. Show what came back and
 *  do not retry.
 *
 *  `count` is any integer >= 1. Over the project's ceiling the call 400s
 *  with the limit named ("Lot size 800 exceeds this project's maximum of
 *  500 for the work queue.") — refused rather than clamped, so that a
 *  short batch always means a short queue. Opening the corrections queue
 *  can 409 when the project has no "Wrong Owner" tag. Both bodies'
 *  `detail` is written for the operator — surface it verbatim rather than
 *  swallowing it or re-phrasing it. */
export async function startEnrichmentSession(
  slug: string,
  body: { queue: EnrichmentQueue; count: number; work_view?: MAWorkView },
): Promise<EnrichmentSessionStartResponse> {
  const res = await apiClient.post<EnrichmentSessionStartResponse>(
    `/projects/${slug}/enrichment/sessions`,
    body,
  );
  return res.data;
}

/** Release everything still held by the current operator. Rows they already
 *  completed released themselves, so this is a no-op on a fully-worked
 *  batch. Idempotent. */
export async function endEnrichmentSession(
  slug: string,
): Promise<{ released: number }> {
  const res = await apiClient.post<{ released: number }>(
    `/projects/${slug}/enrichment/sessions/end`,
  );
  return res.data;
}

/** Fire-and-forget release for page teardown (tab close, hard navigation).
 *  `navigator.sendBeacon` can't carry the Authorization header this API
 *  authenticates with, so we use `fetch` + `keepalive`, which survives the
 *  unload AND carries the token. Best effort: a dropped request just leaves
 *  the rows for the reservation's expiry to reap. Mirrors endCallSessionBeacon. */
export function endEnrichmentSessionBeacon(slug: string): void {
  const token = tokenStorage.getAccess();
  try {
    void fetch(`${getApiBaseUrl()}/projects/${slug}/enrichment/sessions/end`, {
      method: "POST",
      keepalive: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  } catch {
    // Nothing actionable during teardown.
  }
}

/** Drop one row from the current batch. The ONLY release the frontend has
 *  to make by hand — saves and corrections free their row server-side.
 *
 *  A skipped company can come back in a LATER session of the same operator;
 *  it only leaves the current batch. */
export async function skipEnrichmentLead(
  slug: string,
  companyId: number,
): Promise<{ released: boolean }> {
  const res = await apiClient.post<{ released: boolean }>(
    `/projects/${slug}/enrichment/sessions/skip`,
    { company_id: companyId },
  );
  return res.data;
}

/** Who is currently holding rows, across enrichment AND the call center.
 *  Admin-only (`enrichment.manage_rates`). */
export async function listActiveSessions(
  slug: string,
): Promise<SessionHolder[]> {
  const res = await apiClient.get<{ holders?: SessionHolder[] }>(
    `/projects/${slug}/enrichment/sessions/active`,
  );
  return res.data.holders ?? [];
}

/** Force-release every row held by one operator. Admin-only. */
export async function releaseSessionFor(
  slug: string,
  userId: string,
): Promise<{ released: number }> {
  const res = await apiClient.delete<{ released: number }>(
    `/projects/${slug}/enrichment/sessions/${userId}`,
  );
  return res.data;
}

/** Calculated M&A Work views; never CRM pipeline statuses. */
export type MAWorkCategory = "STANDARD" | "MANUAL_EXCEPTION" | "NEEDS_REVIEW" | "BLOCKED" | "OUTSIDE_MANDATE" | "RETRY_REQUIRES_REVIEW" | "EXISTING_CHANNEL_REVIEW" | "BELOW_THRESHOLD";
export type MAWorkView = MAWorkCategory | "WORK";
export interface MAWorkCount { total: number; eligible: number; available: number; reserved: number }
export interface MAWorkSelection {
  policy_version: string; category: MAWorkCategory; operationally_admitted: boolean; commercially_admissible: boolean;
  observable_mandate: string; score_passes: boolean | null; manual_exception: string;
  director_resolved: boolean; treatment_blocked: boolean; eligible: boolean;
  reserved: boolean; available: boolean; ownership: string; complete_mandate_certified: boolean;
  reasons: string[]; provenance: Record<string, string>;
}
export interface MAWorkViewResponse {
  category: MAWorkView; items: { company_id: number; name: string; selection: MAWorkSelection }[];
  total: number; page: number; page_size: number;
}
export async function getMAWorkView(slug: string, category: MAWorkView, page = 1): Promise<MAWorkViewResponse> {
  return (await apiClient.get<MAWorkViewResponse>(`/projects/${slug}/enrichment/work-selection`, { params: { category, page, page_size: 20 } })).data;
}
